import {ChangeDetectorRef, Injectable} from "@angular/core";
import {EventBus} from "../../event/EventBus";
import {ACTIVITY_STATUS, ActivityEvent, ActivityEventData} from "../../event/ActivityEvent";
import {IStep, PreviewStep, QuizStep, ReadAndRecordStep, WordWallStep} from "./step.objects";
import {BookSessionType, ContentProgression} from "../../constants";
import {SessionService} from "../configuration/session.service";
import {StudentConfiguration} from "../assignment/student.configuration";

export enum StepAccessLevel {
    UNLOCKED,
    SELECTION_UNLOCKED,
    LOCKED
}

export enum StepActivity {
    READING,
    RECORDING,
    READ_MY_RECORDING,
    NO_ACTIVITY
}

@Injectable()
export class StepService {
    private currentStep:IStep = new PreviewStep();
    private accessTypes:Array<BookSessionType> = new Array<BookSessionType>();
    private currentAccessLevel:StepAccessLevel = StepAccessLevel.UNLOCKED;
    private currentStepActivity: StepActivity;
    private stepOneBlink: boolean = false;
    private stepTwoBlink: boolean = false;
    private stepThreeBlink: boolean = false;
    private countdownOn: boolean = false;
    private blinkTimer: any;
    private playMyReadingTimer: any;
    private automaticMove: boolean = true;
    private stepComponentChangeRef: ChangeDetectorRef = null;

    constructor(
        private studentConfiguration: StudentConfiguration,
        private eventBus: EventBus,
        private sessionService: SessionService
    ) {
        this.reset();
    }

    private modifyBlinkTimer(value: boolean, stepPointer: string): void {
        // TODO: try passing in changeDetector; try passing in and making class member;
        // if neither of those work, try passing in step component change detector (or maybe try
        // that first)
        if (value) {
            this.blinkTimer = setInterval(() => {
                this[stepPointer] = !this[stepPointer];
                if (this.stepComponentChangeRef) {
                    this.stepComponentChangeRef.detectChanges();
                }
            }, 300);
            this[stepPointer] = value;
        } else if (!value && this.blinkTimer && this[stepPointer]) {
            clearInterval(this.blinkTimer);
            this.blinkTimer = undefined;
            this[stepPointer] = value;
        }
    }

    public initialize():void {
        if (this.studentConfiguration.contentProgression === ContentProgression.UNLOCKED) {
            this.currentAccessLevel = StepAccessLevel.UNLOCKED;
        }
        if (this.studentConfiguration.contentProgression === ContentProgression.LOCKED) {
            this.currentAccessLevel = StepAccessLevel.LOCKED;
        }
        if (this.studentConfiguration.contentProgression === ContentProgression.STUDENT_SELECTED) {
            this.currentAccessLevel = StepAccessLevel.LOCKED;
        }
    }

    /*
    * This forces as step change, without checking the credentials
    */
    public approveStep(step:IStep): void {
        this.currentStep = this.currentStep.getNext();
        this.eventBus.next(this.currentStep.getEvent());
    }

    /*
     * This will allow any step to be set, if credentials are met.
     */
    public requestStep(step:IStep):IStep {

        if (this.checkStepCredentials(step)) {
            const previousStep = this.currentStep;
            this.currentStep = step;
            // RA-5154: Wordwall handles closing the glossary, and we seem to get into an event loop in some cases,
            // so avoid sending an extra event here
            if (previousStep.getType() !== BookSessionType.WORDWALL) {
                this.eventBus.next(
                    new ActivityEvent(ACTIVITY_STATUS.activity_end, new ActivityEventData({
                        type: "glossary"
                    }))
                );
            }
            this.sessionService.sendStepChange(this.currentStep.getType())
                .subscribe(() => {
                    this.eventBus.next(this.currentStep.getEvent());
            });
        } else {
            // if we are blocked from the requested step, it may be due to change from Unlocked to a more restrictive mode
            // if the step requested is not WordWall, we have completed WordWall and should default to Preview step. RA-5404, RA-5405
            if (step.getType() !== BookSessionType.WORDWALL) {
                this.currentStep = new PreviewStep();
                this.sessionService.sendStepChange(this.currentStep.getType())
                    .subscribe(() => {
                        this.eventBus.next(this.currentStep.getEvent());
                    });
            }
        }
        return this.currentStep;
    }

    public makeStepAvailable(type:BookSessionType):void {
        if (this.accessTypes.indexOf(type) === -1) {
            this.accessTypes.push(type);
        }
    }

    public resetAccessTypes(): void {
        this.accessTypes = [];
    }

    public canStep():boolean {
        return this.checkStepCredentials(this.currentStep);
    }

    public canStepNext():boolean {
        return this.checkStepCredentials(this.currentStep.getNext());
    }

    public canUseGlossary():boolean {
        return this.current().getType() === BookSessionType.PREVIEW;
    }

    public setCurrentStepActivity(currentStepActivity: StepActivity): void {
        this.currentStepActivity = currentStepActivity;
    }

    public resetCurrentStepActivity(): void {
        this.currentStepActivity = StepActivity.NO_ACTIVITY;
    }

    public isNonActivity(): boolean {
        return this.currentStepActivity === StepActivity.NO_ACTIVITY;
    }

    public isUnlocked(): boolean {
        return this.currentAccessLevel === StepAccessLevel.UNLOCKED;
    }
    /*
    * This will move to the next step and emit and event, only if the credentials are met.
     */
    public next():IStep {
        if (this.checkStepCredentials(this.currentStep)) {
            this.currentStep = this.currentStep.getNext();
            this.sessionService.sendStepChange(this.currentStep.getType())
                .subscribe(() => {
                    this.eventBus.next(this.currentStep.getEvent());
                });
        }
        return this.currentStep;
    }

    public current():IStep {
        return this.currentStep;
    }

    public reset():IStep {
        this.currentStep = new PreviewStep();

        this.eventBus.next(this.currentStep.getEvent());
        return this.currentStep;
    }

    public checkStepCredentials(step:IStep):boolean {
        return this.isStepAccessible(step);
    }

    public isStepAccessible(requestedStep: IStep): boolean {
        if (this.isUnlocked()) {
            return true;
        }

        let validStep = false;
        this.accessTypes.forEach((type) => {
            if (type === requestedStep.getType()) {
                validStep = true;
                return;
            }
        });
        return validStep;
    }

    public getStepFromType(type:BookSessionType):IStep {
        if (type === BookSessionType.WORDWALL) {
            return new WordWallStep();
        }
        if (type === BookSessionType.PREVIEW) {
            return new PreviewStep();
        }
        if (type === BookSessionType.FLUENCY) {
            return new ReadAndRecordStep();
        }
        if (type === BookSessionType.QUIZ) {
            return new QuizStep();
        }
        return null;
    }

    public get isWordWall():boolean {
        return this.currentStep.getType() === BookSessionType.WORDWALL;
    }

    public get isPreview():boolean {
        return this.currentStep.getType() === BookSessionType.PREVIEW;
    }

    public get isFluency():boolean {
        return this.currentStep.getType() === BookSessionType.FLUENCY;
    }

    public get isQuiz():boolean {
        return this.currentStep.getType() === BookSessionType.QUIZ;
    }

    public get isReading(): boolean {
        return this.currentStepActivity === StepActivity.READING;
    }

    public get isRecording(): boolean {
        return this.currentStepActivity === StepActivity.RECORDING;
    }

    public get isReadingMyRecording(): boolean {
        return this.currentStepActivity === StepActivity.READ_MY_RECORDING;
    }

    public get isCountdownOn(): boolean {
        return this.countdownOn;
    }

    public get startedReadOrRecordActivity(): boolean {
        return (this.isReading || this.isRecording || this.isReadingMyRecording || this.isCountdownOn);
    }

    public set isCountdownOn(value: boolean) {
        this.countdownOn = value;
    }

    public get stepOneStartBlinking(): boolean {
        return this.stepOneBlink;
    }

    public get stepTwoStartBlinking(): boolean {
        return this.stepTwoBlink;
    }

    public get stepThreeStartBlinking(): boolean {
        return this.stepThreeBlink;
    }

    public set changeDetectorRef(ref: ChangeDetectorRef) {
        this.stepComponentChangeRef = ref;
    }

    public setStepOneBlinking(value: boolean): void {
        this.modifyBlinkTimer(value, "stepOneBlink");
    }

    public setStepTwoBlinking(value: boolean): void {
        this.modifyBlinkTimer(value, "stepTwoBlink");
    }

    public setStepThreeBlinking(value: boolean): void {
        this.modifyBlinkTimer(value, "stepThreeBlink");
    }

    public setAutomaticMove(automaticMove: boolean): void {
        this.automaticMove = automaticMove;
    }

    public continueAutomaticMove(): boolean {
        return this.automaticMove;
    }

    public isBlinking(): boolean {
        return !!this.blinkTimer;
    }

}
